import React from 'react';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  Button,
  HStack,
  Link,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { useQuery } from '@apollo/client';
import { GET_TENANT_CONTACT_FOR_AUTH } from '../queries';
import { type AccessBlockedInfo } from '../types';

interface AccessBlockedCardProps {
  info: AccessBlockedInfo;
  // Theme colors
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  accentBlue: string;
  // Handlers
  onTryDifferent: () => void;
}

/**
 * Rendered when a login path answers ACCESS_BLOCKED (2026-09-04): the person proved
 * their credential, but sign-in for this account has been switched off — because
 * their employee record is TERMINATED, or an admin blocked it by hand. The backend
 * deliberately never tells the browser WHICH, and this card never guesses: the copy
 * is neutral and points them at the people who can answer.
 *
 * Also shown on /login?blocked=1 — where the Apollo error link sends someone whose
 * EXISTING session was refused mid-use (their token is cleared on the way).
 *
 * Sibling of NoAccountFoundCard: tenant copy from currentTenant, no hard-coded
 * tenant strings, ships unchanged across every tenant via Module Sync.
 */
export const AccessBlockedCard: React.FC<AccessBlockedCardProps> = ({
  info,
  textPrimary,
  textSecondary,
  textMuted,
  accentBlue,
  onTryDifferent,
}) => {
  const { data } = useQuery(GET_TENANT_CONTACT_FOR_AUTH, { fetchPolicy: 'cache-first' });
  const tenantName = data?.currentTenant?.name || 'this site';
  const supportEmail =
    data?.currentTenant?.emailConfig?.replyToEmail ||
    data?.currentTenant?.emailConfig?.fromEmail ||
    '';

  const subject = encodeURIComponent(`Sign-in switched off${info.identifier ? ` — ${info.identifier}` : ''}`);
  const body = encodeURIComponent(
    `Hi ${tenantName} team,\n\n` +
      `I tried to sign in${info.identifier ? ` as ${info.identifier}` : ''} and the system says sign-in for my account has been switched off. ` +
      `Could you let me know why, or switch it back on if this is a mistake?\n\n` +
      `Thanks.`
  );
  const mailto = supportEmail ? `mailto:${supportEmail}?subject=${subject}&body=${body}` : undefined;

  const wasSession = info.method === 'session';

  return (
    <VStack align="stretch" spacing={4}>
      <Alert status="error" variant="left-accent" borderRadius="md" flexDirection="column" alignItems="flex-start">
        <HStack spacing={2} align="flex-start">
          <AlertIcon mt={1} />
          <Box>
            <AlertTitle color={textPrimary}>{wasSession ? 'You have been signed out' : 'Sign-in switched off'}</AlertTitle>
            <AlertDescription color={textSecondary} fontSize="sm">
              {wasSession
                ? <>Sign-in for your account on <strong>{tenantName}</strong> has been switched off, so your session ended.</>
                : <>Sign-in for {info.identifier ? <>the account <strong>{info.identifier}</strong></> : 'this account'} on{' '}
                    <strong>{tenantName}</strong> has been switched off.</>}
            </AlertDescription>
          </Box>
        </HStack>
      </Alert>

      <Text fontSize="sm" color={textSecondary}>
        If you think this is a mistake, contact your manager or <strong>{tenantName}</strong> and ask them to check your account.
      </Text>

      {supportEmail ? (
        <Tooltip label={`Open your mail client and email ${supportEmail}`} hasArrow openDelay={300}>
          <Button as={Link} href={mailto} bg={accentBlue} color="white" width="100%" _hover={{ textDecoration: 'none' }}>
            ✉️ Email {tenantName}
          </Button>
        </Tooltip>
      ) : (
        <Text fontSize="xs" color={textMuted} textAlign="center">
          {tenantName} hasn't set a public support address yet — speak to your manager.
        </Text>
      )}

      <HStack spacing={3} justify="center" wrap="wrap">
        <Tooltip label="Go back and sign in with a different account" hasArrow openDelay={300}>
          <Link color={accentBlue} fontSize="sm" onClick={onTryDifferent} cursor="pointer">
            Sign in with a different account
          </Link>
        </Tooltip>
      </HStack>
    </VStack>
  );
};
